import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Calendar, Heart, DollarSign, Star, AlertCircle, CheckCircle2, Clock,
  MapPin, Phone, MessageCircle, X
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import ChatDrawer from '../components/ChatDrawer';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const t = localStorage.getItem('khadam_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const ReviewModal = ({ booking, onClose, onSubmitted }) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!text.trim()) {
      setError(t('clientDashboard.review.textRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(
        `${API}/reviews`,
        {
          booking_id: booking.id,
          provider_id: booking.provider_id,
          rating,
          text: text.trim(),
        },
        { headers: authHeaders() }
      );
      onSubmitted();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="review-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-lg text-gray-900">{t('clientDashboard.review.title')}</h3>
            <p className="text-xs text-gray-500">{booking.provider_name} · {booking.date}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full" aria-label={t('common.close')}>
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{t('clientDashboard.review.rating')}</label>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  onClick={() => setRating(n)}
                  data-testid={`star-${n}`}
                  className="text-3xl transition-transform hover:scale-110"
                >
                  <Star size={32} className={n <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} />
                </button>
              ))}
              <span className="ml-2 font-bold text-gray-700">{rating}/5</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{t('clientDashboard.review.comment')}</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              data-testid="review-text"
              placeholder={t('clientDashboard.review.placeholder')}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none text-sm"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2 pt-2">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 text-sm font-semibold"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="submit-review-btn"
              className="flex-1 px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-xl disabled:opacity-50 text-sm font-bold"
            >
              {submitting ? t('common.loading') : t('clientDashboard.review.submit')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ClientDashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviewBooking, setReviewBooking] = useState(null);
  const [chatPeer, setChatPeer] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/clients/me/dashboard`, { headers: authHeaders() });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated || user?.role === 'provider' || user?.role === 'admin') {
      setLoading(false);
      return;
    }
    load();
    // eslint-disable-next-line
  }, [isAuthenticated, user]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-white pt-20 px-4">
        <div className="bg-white rounded-2xl shadow-xl p-10 text-center max-w-md">
          <AlertCircle size={48} className="text-orange-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('clientDashboard.notLogged')}</h2>
          <button
            onClick={() => navigate('/')}
            data-testid="client-dash-home"
            className="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl px-6 py-3 transition"
          >
            {t('register.backHome')}
          </button>
        </div>
      </div>
    );
  }

  if (user?.role === 'provider') {
    navigate('/dashboard');
    return null;
  }
  if (user?.role === 'admin') {
    navigate('/admin');
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20 px-4">
        <p className="text-red-600 font-medium">{error || t('common.error')}</p>
      </div>
    );
  }

  const statusLabel = (s) => {
    if (s === 'confirmed') return t('dashboard.bookings.statusConfirmed');
    if (s === 'completed') return t('dashboard.bookings.statusCompleted');
    if (s === 'cancelled') return t('dashboard.bookings.statusCancelled');
    return s;
  };
  const statusClass = (s) => {
    if (s === 'confirmed') return 'bg-blue-100 text-blue-700';
    if (s === 'completed') return 'bg-green-100 text-green-700';
    if (s === 'cancelled') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };
  const whatsappLink = (phone) => `https://wa.me/${(phone || '').replace(/\D/g, '')}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50/40 via-white to-white pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <p className="text-sm text-gray-500 mb-1">{t('dashboard.welcome')},</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900" data-testid="client-dash-title">
            {data.user.name}
          </h1>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5" data-testid="stat-total-bookings">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center mb-3">
              <Calendar size={18} className="text-blue-600" />
            </div>
            <p className="text-xs text-gray-500 mb-1 font-medium">{t('clientDashboard.stats.totalBookings')}</p>
            <p className="text-2xl font-bold text-gray-900">{data.stats.total_bookings}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5" data-testid="stat-total-spent">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center mb-3">
              <DollarSign size={18} className="text-emerald-600" />
            </div>
            <p className="text-xs text-gray-500 mb-1 font-medium">{t('clientDashboard.stats.totalSpent')}</p>
            <p className="text-2xl font-bold text-gray-900">{data.stats.total_spent} <span className="text-sm font-normal text-gray-500">DH</span></p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5" data-testid="stat-reviews-written">
            <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center mb-3">
              <Star size={18} className="text-yellow-600" />
            </div>
            <p className="text-xs text-gray-500 mb-1 font-medium">{t('clientDashboard.stats.reviewsWritten')}</p>
            <p className="text-2xl font-bold text-gray-900">{data.stats.reviews_written}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Bookings list */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="font-bold text-lg text-gray-900">{t('clientDashboard.bookings.title')}</h2>
            </div>
            {data.bookings.length === 0 ? (
              <div className="p-12 text-center">
                <Calendar size={40} className="text-gray-300 mx-auto mb-3" />
                <p className="font-medium text-gray-700 mb-1">{t('clientDashboard.bookings.empty')}</p>
                <Link
                  to="/"
                  data-testid="explore-providers-link"
                  className="inline-block mt-4 px-5 py-2 bg-blue-500 text-white text-sm font-semibold rounded-xl hover:bg-blue-600"
                >
                  {t('clientDashboard.bookings.exploreProviders')}
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-gray-100" data-testid="client-bookings-list">
                {data.bookings.map((b) => (
                  <div key={b.id} className="p-5 hover:bg-gray-50 transition" data-testid={`client-booking-${b.id}`}>
                    <div className="flex items-start gap-4">
                      <img src={b.provider_photo} alt={b.provider_name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-semibold text-gray-900">{b.provider_name}</span>
                          <span className="text-xs text-gray-400">·</span>
                          <span className="text-xs text-gray-500">{b.provider_skill}</span>
                          <span className={`ml-auto inline-flex text-xs font-semibold px-2 py-0.5 rounded-full ${statusClass(b.status)}`}>
                            {statusLabel(b.status)}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
                          <span className="inline-flex items-center gap-1"><Clock size={12} />{b.date} · {b.time}</span>
                          <span className="inline-flex items-center gap-1"><DollarSign size={12} />{b.total_client} DH</span>
                        </div>
                        {b.description && <p className="text-sm text-gray-600 mb-3 line-clamp-2">{b.description}</p>}
                        <div className="flex items-center gap-2 flex-wrap">
                          {b.already_reviewed ? (
                            <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-3 py-1.5 rounded-full font-medium">
                              <CheckCircle2 size={12} /> {t('clientDashboard.bookings.reviewed')}
                            </span>
                          ) : (
                            <button
                              onClick={() => setReviewBooking(b)}
                              data-testid={`review-btn-${b.id}`}
                              className="inline-flex items-center gap-1 text-xs bg-yellow-50 hover:bg-yellow-100 text-yellow-700 px-3 py-1.5 rounded-full font-semibold"
                            >
                              <Star size={12} /> {t('clientDashboard.bookings.leaveReview')}
                            </button>
                          )}
                          <Link
                            to={`/booking/${b.provider_id}`}
                            data-testid={`rebook-btn-${b.id}`}
                            className="inline-flex items-center gap-1 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full font-semibold"
                          >
                            <Calendar size={12} /> {t('clientDashboard.bookings.rebook')}
                          </Link>
                          {b.provider_user_id && (
                            <button
                              onClick={() => setChatPeer({
                                userId: b.provider_user_id,
                                name: b.provider_name,
                                photo: b.provider_photo,
                                skill: b.provider_skill,
                                bookingId: b.id,
                              })}
                              data-testid={`message-btn-${b.id}`}
                              className="inline-flex items-center gap-1 text-xs bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-3 py-1.5 rounded-full font-semibold"
                            >
                              <MessageCircle size={12} /> {t('chat.contactProvider')}
                            </button>
                          )}
                          <a
                            href={whatsappLink(b.provider_phone || '')}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`whatsapp-btn-${b.id}`}
                            className="inline-flex items-center gap-1 text-xs bg-green-50 hover:bg-green-100 text-green-700 px-3 py-1.5 rounded-full font-semibold"
                          >
                            <MessageCircle size={12} /> WhatsApp
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Favorites */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 self-start">
            <div className="flex items-center gap-2 mb-5">
              <Heart size={18} className="text-red-500 fill-red-500" />
              <h2 className="font-bold text-lg text-gray-900">{t('clientDashboard.favorites.title')}</h2>
            </div>
            {data.favorite_providers.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">{t('clientDashboard.favorites.empty')}</p>
            ) : (
              <div className="space-y-3" data-testid="favorites-list">
                {data.favorite_providers.map((fp) => (
                  <Link
                    key={fp.provider_id}
                    to={`/booking/${fp.provider_id}`}
                    data-testid={`favorite-${fp.provider_id}`}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition group"
                  >
                    <img src={fp.photo} alt={fp.name} className="w-11 h-11 rounded-full object-cover flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-gray-900 truncate group-hover:text-blue-600">{fp.name}</p>
                      <p className="text-xs text-gray-500 truncate">{fp.skill}</p>
                    </div>
                    <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full flex-shrink-0">
                      {fp.bookings_count}×
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {reviewBooking && (
        <ReviewModal
          booking={reviewBooking}
          onClose={() => setReviewBooking(null)}
          onSubmitted={load}
        />
      )}

      {chatPeer && (
        <ChatDrawer
          open={!!chatPeer}
          onClose={() => setChatPeer(null)}
          peerUserId={chatPeer.userId}
          peerName={chatPeer.name}
          peerPhoto={chatPeer.photo}
          peerSkill={chatPeer.skill}
          bookingId={chatPeer.bookingId}
        />
      )}

      <Footer />
    </div>
  );
};

export default ClientDashboardPage;
